import get from 'lodash/get';
import axiosConfig from './axiosConfig';

const snakeToCamel = (key) => key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

/** Normalize API keys (snake_case → camelCase) without altering already-camelCase fields. */
const normalizeKeys = (value) => {
  if (value == null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(normalizeKeys);
  return Object.entries(value).reduce((acc, [key, nested]) => {
    const normalizedKey = key.includes('_') ? snakeToCamel(key) : key;
    acc[normalizedKey] = normalizeKeys(nested);
    return acc;
  }, {});
};

const getRawPayload = (res) => {
  const top = get(res, 'data', null);
  const raw =
    get(res, ['data', 'data'], null) ??
    (top && typeof top === 'object' && !Array.isArray(top) ? top : null);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw;
};

const unwrap = (res, sectionKey) => {
  const raw = getRawPayload(res);
  if (!raw) return null;
  // Prefer an explicit section wrapper when present; otherwise use the payload as-is.
  // Do not fall through to nested `shiftOverview` — daily responses embed that object.
  const wrapped =
    sectionKey && raw[sectionKey] && typeof raw[sectionKey] === 'object' && !Array.isArray(raw[sectionKey])
      ? raw[sectionKey]
      : null;
  const payload = wrapped || (raw.meta || raw.kpis ? raw : null) || raw.overview || raw.dashboard || raw;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  return normalizeKeys(payload);
};

/**
 * @see Spacer Log Dashboard API — Frontend Integration Guide
 */
export const fetchSpacerLogOverview = async ({
  projectId,
  periodType = 'month',
  anchorDate,
  sowId = 'all',
}) => {
  const params = new URLSearchParams({
    projectId: String(projectId),
    periodType,
    anchorDate,
  });
  if (sowId != null && sowId !== '') {
    params.set('sowId', String(sowId));
  }
  const res = await axiosConfig.get(`spacer-log/dashboard/overview?${params.toString()}`);
  const data = unwrap(res, 'overview');
  if (!data) {
    throw new Error('Spacer log overview response was empty or invalid.');
  }
  return data;
};

export const fetchSpacerLogDaily = async ({ projectId, sowId, logDate, shift }) => {
  const params = new URLSearchParams({
    projectId: String(projectId),
    sowId: String(sowId),
    logDate,
  });
  if (shift != null && shift !== '') {
    params.set('shift', String(shift));
  }
  const res = await axiosConfig.get(`spacer-log/dashboard/daily?${params.toString()}`);
  const data = unwrap(res, 'daily');
  if (!data) {
    throw new Error('Spacer log daily response was empty or invalid.');
  }
  return data;
};

export const fetchSpacerLogShiftOverview = async ({ projectId, sowId, logDate, shift = 'both' }) => {
  const params = new URLSearchParams({
    projectId: String(projectId),
    sowId: String(sowId),
    logDate,
    shift: String(shift),
  });
  const res = await axiosConfig.get(`spacer-log/dashboard/shift-overview?${params.toString()}`);
  const data = unwrap(res, 'shiftOverview');
  if (!data) {
    throw new Error('Spacer log shift overview response was empty or invalid.');
  }
  return data;
};
